<template>
  <v-dialog
    :value="enableDialog"
    persistent
    width="800"
    @keydown.esc="closeButtonClickEvent"
    @close="closeButtonClickEvent"
  >
    <validation-observer
      ref="traderLocationFormValidation"
    >
      <v-card>
        <v-toolbar flat>
          <v-toolbar-title>事業場設定</v-toolbar-title>
        </v-toolbar>
        <v-divider />
        <v-card-text
          style="max-height: 60vh;overflow-y: auto" class="pt-2"
        >
          <v-row dense>
            <v-col cols="12" class="text-right">
              <v-btn
                class="primary"
                @click="copyFromTraderButtonClickEvent"
              >
                JWNET未加入業者情報をコピー
              </v-btn>
            </v-col>
          </v-row>
          <v-row dense>
            <v-col cols="12">
              <validation-provider
                v-slot="{ errors }"
                class="requiredMark"
                name="事業場コード"
                rules="required|alpha_num_only|max:10"
              >
                <v-text-field
                  v-model="forms.code"
                  :error-messages="errors"
                  :disabled="targetId"
                  dense
                  hide-details="auto"
                  label="事業場コード"
                  hint="半角英数字のみ10文字以内"
                  outlined
                  persistent-hint
                  persistent-placeholder
                  placeholder="例）NL01"
                >
                  <template v-slot:label>
                    <span>事業場コード</span>
                    <span class="ml-1">
                      ({{ forms.code?.length || 0 }} / 10)
                    </span>
                  </template>
                  <template v-slot:counter></template>
                </v-text-field>
              </validation-provider>
            </v-col>
          </v-row>

          <v-row dense>
            <v-col cols="12">
              <validation-provider
                v-slot="{ errors }"
                class="requiredMark"
                name="事業場名"
                rules="required|max:60"
              >
                <v-text-field
                  v-model="forms.name"
                  :error-messages="errors"
                  dense
                  hide-details="auto"
                  label="事業場名"
                  outlined
                  persistent-placeholder
                  placeholder="例）△△△△　株式会社"
                >
                  <template v-slot:label>
                    <span>事業場名</span>
                    <span class="ml-1">
                      ({{ forms.name?.length || 0 }} / 60)
                    </span>
                  </template>
                  <template v-slot:counter></template>
                </v-text-field>
              </validation-provider>
            </v-col>
          </v-row>


          <v-row dense>
            <v-col cols="12" md="9">
              <validation-provider
                v-slot="{ errors }"
                :rules="{zip_code: true, required: true, max:8 }"
                class="requiredMark"
                name="郵便番号"
              >
                <v-text-field
                  v-model="forms.zip_code"
                  :error-messages="errors"
                  dense
                  label="郵便番号"
                  hide-details="auto"
                  hint="ハイフンを含めて入力してください"
                  outlined
                  persistent-hint
                  persistent-placeholder
                  placeholder="例）111-1111"
                  required
                >
                  <template v-slot:label>
                    <span>郵便番号</span>
                    <span class="ml-1">
                      ({{ forms.zip_code?.length || 0 }} / 8)
                    </span>
                  </template>
                  <template v-slot:counter></template>
                  <template v-slot:append-outer>
                    <zip-code-search
                      :zip-code="forms.zip_code"
                      @zipAddress="setAddress"
                    />
                    &nbsp;
                    <v-btn
                      outlined
                      small
                      @click="searchZipButtonClickEvent"
                    >
                      住所→郵便番号
                    </v-btn>
                  </template>
                </v-text-field>
              </validation-provider>
            </v-col>
          </v-row>
          <!-- /郵便番号 -->
          <!-- 都道府県／市区町村 -->
          <v-row dense>
            <v-col cols="12" md="4">
              <validation-provider
                v-slot="{ errors }"
                class="requiredMark"
                name="都道府県"
                rules="required"
              >
                <v-autocomplete
                  v-model="forms.prefecture_code"
                  :error-messages="errors"
                  :items="prefList"
                  dense
                  hide-details="auto"
                  item-text="text"
                  item-value="value"
                  label="都道府県"
                  outlined
                  persistent-placeholder
                  placeholder="例）東京都"
                />
              </validation-provider>
            </v-col>
            <v-col cols="12" md="6">
              <validation-provider
                v-slot="{ errors }"
                class="requiredMark"
                name="市区町村"
                rules="required|full_width|max:20"
              >
                <v-text-field
                  v-model="forms.address_1"
                  :error-messages="errors"
                  dense
                  label="市区町村"
                  hide-details="auto"
                  outlined
                  persistent-placeholder
                  placeholder="例）中央区"
                >
                  <template v-slot:label>
                    <span>市区町村</span>
                    <span class="ml-1">
                      ({{ forms.address_1?.length || 0 }} / 20)
                    </span>
                  </template>
                  <template v-slot:counter></template>
                </v-text-field>
              </validation-provider>
            </v-col>
          </v-row>
          <!-- /都道府県／市区町村 -->
          <!-- 町域 -->
          <v-row dense>
            <v-col cols="12" md="10">
              <validation-provider
                v-slot="{ errors }"
                name="町域"
                rules="full_width|max:40"
              >
                <v-text-field
                  v-model="forms.address_2"
                  :error-messages="errors"
                  dense
                  label="町域"
                  hide-details="auto"
                  outlined
                  persistent-placeholder
                  placeholder="例）日本橋兜町"
                >
                  <template v-slot:label>
                    <span>町域</span>
                    <span class="ml-1">
                      ({{ forms.address_2?.length || 0 }} / 40)
                    </span>
                  </template>
                  <template v-slot:counter></template>
                </v-text-field>
              </validation-provider>
            </v-col>
          </v-row>
          <!-- /町域 -->
          <!-- 詳細住所 -->
          <v-row dense>
            <v-col cols="12" md="10">
              <validation-provider
                v-slot="{ errors }"
                name="詳細住所"
                rules="max:50"
              >
                <v-text-field
                  v-model="forms.address_3"
                  :error-messages="errors"
                  dense
                  label="詳細住所"
                  hide-details="auto"
                  outlined
                  persistent-placeholder
                  placeholder="例）１−２−３　○○ビル○階"
                >
                  <template v-slot:label>
                    <span>詳細住所</span>
                    <span class="ml-1">
                      ({{ forms.address_3?.length || 0 }} / 50)
                    </span>
                  </template>
                  <template v-slot:counter></template>
                </v-text-field>
              </validation-provider>
            </v-col>
          </v-row>
          <!-- /詳細住所 -->
          <!-- 電話番号 -->
          <v-row dense>
            <v-col cols="12" md="6">
              <validation-provider
                v-slot="{ errors }"
                name="電話番号"
                :rules="{ numeric_hyphen_only: true,min: 2, max: 15 }"
              >
                <v-text-field
                  v-model="forms.phone_number"
                  :error-messages="errors"
                  dense
                  label="電話番号"
                  hint="電話番号を「-」ハイフン「( )」込みで入力します"
                  persistent-hint
                  hide-details="auto"
                  outlined
                  persistent-placeholder
                  placeholder="例）000-0000-0000"
                >
                  <template v-slot:label>
                    <span>電話番号</span>
                    <span class="ml-1">
                      ({{ forms.phone_number?.length || 0 }} / 15)
                    </span>
                  </template>
                  <template v-slot:counter></template>
                </v-text-field>
              </validation-provider>
            </v-col>
          </v-row>
          <!-- /電話番号 -->
          <!-- 事業場区分 -->
          <v-row dense>
            <v-col cols="12" md="8">
              <v-radio-group
                v-model="forms.location_type"
                label="事業場区分"
                row
                dense
                hide-details="auto"
                class="pa-0 ma-0"
              >
                <v-radio
                  v-if="masterTrader.subscriber_type === 'transport_type'"
                  label="積替・保管"
                  value="transshipment"
                />
                <v-radio
                  v-if="masterTrader.subscriber_type === 'disposer_type'"
                  label="中間処理のみ"
                  value="only_middle"
                />
                <v-radio
                  v-if="masterTrader.subscriber_type === 'disposer_type'"
                  label="最終処分のみ"
                  value="only_last"
                />
                <v-radio
                  v-if="masterTrader.subscriber_type === 'disposer_type'"
                  label="中間処理＋最終処分"
                  value="middle_and_last"
                />
              </v-radio-group>
            </v-col>
          </v-row>
          <!-- /事業場区分 -->
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn @click="closeButtonClickEvent">
            閉じる
          </v-btn>
          <v-btn
            color="primary"
            @click="registerButtonClickEvent"
          >
            登録
          </v-btn>
        </v-card-actions>
      </v-card>
    </validation-observer>
    <search-zip-modal
      :enable="enableSearchZipModal"
      :initial-prefecture-code="forms.prefecture_code"
      :initial-city-name="forms.address_1"
      :initial-town-name="forms.address_2"
      @reflectFullCallback="searchZipReflectCallback"
      @reflectZipCallback="searchZipReflectZipCallback"
      @closeCallback="closeModalCallback"
    />
  </v-dialog>
</template>

<script>
import { mapActions } from 'vuex'
import PrefectureList from '../../../../../mixins/PrefectureList'
import ZipCodeSearch from '../../../../../components/ZipCodeSearch'
import SearchZipModal from "../../../../components/SearchZipModal.vue";

export default {
  name: 'TraderLocationDialog',
  components: {
    ZipCodeSearch,
    SearchZipModal
  },
  mixins: [ PrefectureList ],
  props: {
    enableDialog: {
      type: Boolean,
      default: () => false
    },
    masterTrader: {
      type: Object,
      default: () => {},
    },
    traderId: {
      type: Number,
      default: () => null,
    },
    targetId: {
      type: Number,
      default: () => null,
    },
  },
  data() {
    return {
      forms: {
        id: null,
        name: null,
        code: null,
        zip_code: null,
        prefecture_code: null,
        address_1: null,
        address_2: null,
        address_3: null,
        phone_number: null,
        location_type: null,
      },
      enableSearchZipModal: false,
    }
  },
  computed: {},
  watch: {
    enableDialog (newVal) {
      this.$nextTick(() => this.$refs.traderLocationFormValidation.reset())
      Object.assign(this.$data.forms, this.$options.data().forms);
      if (newVal) {
        if (this.targetId) {
          this.searchEditLocation()
        } else {
          this.searchNewLocation()
        }
      }
    },
  },
  created() {
  },
  methods: {
    ...mapActions('DriverMasterNonSubscribeTraderLocation', {
      newTraderLocation: 'new',
      editTraderLocation: 'edit',
      createTraderLocation: 'create',
      updateTraderLocation: 'update',
    }),
    searchZipButtonClickEvent () {
      this.enableSearchZipModal = true;
    },
    copyFromTraderButtonClickEvent () {
      this.forms.name = this.masterTrader.name
      this.forms.zip_code = this.masterTrader.zip_code
      this.forms.prefecture_code = this.masterTrader.prefecture_code
      this.forms.address_1 = this.masterTrader.address_1
      this.forms.address_2 = this.masterTrader.address_2
      this.forms.address_3 = this.masterTrader.address_3
      this.forms.phone_number = this.masterTrader.phone_number
    },
    /**
     * 新規時表示情報取得
     */
    searchNewLocation () {
      this.newTraderLocation({ trader_id: this.traderId }).then((content) => {
        this.forms = content
      })
    },
    /**
     * 編集時表示情報取得
     */
    searchEditLocation () {
      this.editTraderLocation({ trader_id: this.traderId, id: this.targetId }).then((content) => {
        this.forms = content
      })
    },
    /**
     * 閉じるボタン クリックイベント
     */
    closeButtonClickEvent () {
      this.$emit('closeEvent')
    },
    /**
     * 登録ボタン クリックイベント
     */
    registerButtonClickEvent () {
      let $self = this
      this.$refs.traderLocationFormValidation.validate().then((result) => {
        if(result){
          $self.sendUploadInformation()
        }
      })
    },
    sendUploadInformation(){
      if (this.targetId) {
        this.updateTraderLocation({ trader_id: this.traderId, id: this.targetId, params: this.forms}).then(() => {
          this.$emit('registerEvent')
        })
      } else {
        this.createTraderLocation({ trader_id: this.traderId, params: this.forms }).then(() => {
          this.$emit('registerEvent')
        })
      }
    },
    setAddress (content) {
      this.forms.prefecture_code = `${content.pref_code}`
      this.forms.address_1 = `${content.locality}`
      this.forms.address_2 = `${content.street}`
    },
    closeModalCallback () {
      this.enableSearchZipModal = false;
    },
    /**
     * 郵便番号検索反映ボタン コールバック
     * @param address { Object }
     * @property address.zip_code { string } 郵便番号
     * @property address.prefecture_code { number } 都道府県コード
     * @property address.city { string } 市区町村
     * @property address.town { string } 町域
     */
    searchZipReflectCallback (address) {
      this.forms.zip_code = address.zip_code
      this.forms.prefecture_code = `${address.prefecture_code}`
      this.forms.address_1 = address.city
      this.forms.address_2 = address.town
    },
    searchZipReflectZipCallback (zipCode) {
      this.forms.zip_code = zipCode;
    },
  }
}
</script>

<style scoped lang="scss">

</style>
